import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { viewExperienceCenter } from "../../Redux/Actions/experienceCenterActions";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import "./viewExperienceCenter.css";

const MAP_LIBRARIES = ["places"];
const MAP_CONTAINER_STYLE = { width: "100%", height: "260px", borderRadius: "10px" };
const DEFAULT_CENTER = { lat: 28.6139, lng: 77.209 };

const ViewExperienceCenter = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const [centerData, setCenterData] = useState(null);
    const [pageLoading, setPageLoading] = useState(false);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: MAP_LIBRARIES,
    });

    const fetchDetails = async () => {
        try {
            setPageLoading(true);
            const res = await dispatch(viewExperienceCenter(id));
            if (res && res.statusCode === 200) {
                setCenterData(res.data);
            } else {
                toast.error(res?.message || "Failed to load experience center details");
            }
        } catch {
            toast.error("Error fetching experience center details");
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [dispatch, id]); // eslint-disable-line

    const coords = centerData?.location?.coordinates;
    const hasCoords = Array.isArray(coords) && coords.length === 2 && (coords[0] !== 0 || coords[1] !== 0);
    const markerPos = hasCoords ? { lat: coords[1], lng: coords[0] } : null;
    const mapCenter = markerPos || DEFAULT_CENTER;

    if (pageLoading) {
        return (
            <MainLayout>
                <div className="vec-loader-wrap">
                    <div className="vec-loader" />
                    <p className="vec-loader-text">Loading experience center data…</p>
                </div>
            </MainLayout>
        );
    }

    if (!centerData) {
        return (
            <MainLayout>
                <div className="vec-empty-wrap">
                    <div className="vec-empty-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                    </div>
                    <p className="vec-empty-text">No Experience Center Data Found.</p>
                    <Link to="/experience_center" className="vec-back-link">← Back to List</Link>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="vec-wrapper">
                <div className="vec-header">
                    <div className="vec-header-left">
                        <Link to="/experience_center" className="vec-back-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </Link>
                        <div className="vec-header-divider" />
                        <div>
                            <div className="vec-breadcrumb">Experience Center / List / View</div>
                            <h1 className="vec-title">{centerData?.title || "No Data"}</h1>
                        </div>
                    </div>
                    <Link to={`/experience_center/edit/${id}`} className="vec-edit-btn">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Edit Center
                    </Link>
                </div>

                <div className="vec-hero-card">
                    <div className="vec-hero-left">
                        {centerData?.cover_image ? (
                            <div className="vec-hero-img-wrap">
                                <img src={centerData.cover_image} alt={centerData.title} className="vec-hero-img" />
                            </div>
                        ) : (
                            <div className="vec-hero-avatar">
                                {centerData?.title?.charAt(0)?.toUpperCase() || "E"}
                            </div>
                        )}
                        <div>
                            <span className="vec-hero-label">Center Name</span>
                            <span className="vec-hero-name">{centerData?.title || "—"}</span>
                            {centerData?.center_code && (
                                <span className="vec-hero-code">{centerData.center_code}</span>
                            )}
                        </div>
                    </div>
                    <div className="vec-hero-meta">
                        {centerData?.status && (
                            <div className={`vec-status-chip vec-status-chip--${String(centerData.status).toLowerCase()}`}>
                                {centerData.status}
                            </div>
                        )}
                        {(centerData?.city?.city_name || centerData?.state?.state_name || centerData?.country?.country_name) && (
                            <div className="vec-hero-meta-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                                {[centerData?.city?.city_name, centerData?.state?.state_name, centerData?.country?.country_name].filter(Boolean).join(", ")}
                            </div>
                        )}
                    </div>
                </div>

                <div className="vec-grid-layout">
                    <div className="vec-col">
                        <div className="vec-card">
                            <div className="vec-card-header">
                                <span className="vec-card-badge">01</span>
                                <h2 className="vec-card-title">Basic Information</h2>
                            </div>
                            <div className="vec-info-grid">
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Title</span>
                                    <span className="vec-info-value">{centerData?.title || "No Data"}</span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Type</span>
                                    <span className="vec-info-value">
                                        {centerData?.type === "coming_soon"
                                            ? "Coming Soon"
                                            : centerData?.type === "available"
                                                ? "Available"
                                                : "No Data"}
                                    </span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Center Code</span>
                                    <span className="vec-info-value">{centerData?.center_code || "No Data"}</span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Open Time</span>
                                    <span className="vec-info-value">
                                        {centerData?.open_timing
                                            ? new Date(centerData.open_timing).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                            : "No Data"}
                                    </span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Close Time</span>
                                    <span className="vec-info-value">
                                        {centerData?.close_timing
                                            ? new Date(centerData.close_timing).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                            : "No Data"}
                                    </span>
                                </div>
                                <div className="vec-info-item vec-info-item--full">
                                    <span className="vec-info-label">Description</span>
                                    <span className="vec-info-value">{centerData?.description || "No Data"}</span>
                                </div>
                                <div className="vec-info-item vec-info-item--full">
                                    <span className="vec-info-label">Cover Image</span>
                                    {centerData?.cover_image ? (
                                        <div className="vec-cover-preview">
                                            <img src={centerData.cover_image} alt="Cover" className="vec-cover-preview-img" />
                                        </div>
                                    ) : (
                                        <span className="vec-info-value">No Data</span>
                                    )}
                                </div>
                                <div className="vec-info-item vec-info-item--full">
                                    <span className="vec-info-label">Video</span>
                                    {centerData?.video ? (
                                        <div className="vec-video-wrap">
                                            <video src={centerData.video} className="vec-video-player" controls />
                                        </div>
                                    ) : (
                                        <span className="vec-info-value">No Data</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="vec-card">
                            <div className="vec-card-header">
                                <span className="vec-card-badge">02</span>
                                <h2 className="vec-card-title">Images</h2>
                                {centerData?.image?.length > 0 && (
                                    <span className="vec-image-count">{centerData.image.length} / 5</span>
                                )}
                            </div>
                            {Array.isArray(centerData?.image) && centerData.image.length > 0 ? (
                                <div className="vec-image-grid">
                                    {centerData.image.map((url, index) => (
                                        <div key={index} className="vec-image-thumb-wrap" onClick={() => window.open(url, "_blank")}>
                                            <img src={url} alt={`center-img-${index + 1}`} className="vec-image-thumb" />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="vec-no-images">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                        <circle cx="8.5" cy="8.5" r="1.5" />
                                        <polyline points="21 15 16 10 5 21" />
                                    </svg>
                                    <p>No images uploaded</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="vec-col">
                        <div className="vec-card">
                            <div className="vec-card-header">
                                <span className="vec-card-badge">03</span>
                                <h2 className="vec-card-title">Address Details</h2>
                            </div>
                            <div className="vec-info-grid">
                                <div className="vec-info-item vec-info-item--full">
                                    <span className="vec-info-label">Address</span>
                                    <span className="vec-info-value">{centerData?.address || "No Data"}</span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Country</span>
                                    <span className="vec-info-value">{centerData?.country?.country_name || "No Data"}</span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">State</span>
                                    <span className="vec-info-value">{centerData?.state?.state_name || "No Data"}</span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">City</span>
                                    <span className="vec-info-value">{centerData?.city?.city_name || "No Data"}</span>
                                </div>
                            </div>
                        </div>

                        <div className="vec-card">
                            <div className="vec-card-header">
                                <span className="vec-card-badge">04</span>
                                <h2 className="vec-card-title">Location</h2>
                                {centerData?.google_url && (
                                    <a href={centerData.google_url} target="_blank" rel="noopener noreferrer" className="vec-maps-link">
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                            <polyline points="15 3 21 3 21 9" />
                                            <line x1="10" y1="14" x2="21" y2="3" />
                                        </svg>
                                        Open in Maps
                                    </a>
                                )}
                            </div>

                            {hasCoords && (
                                <div className="vec-coords-row">
                                    <div className="vec-coord-chip">
                                        <span className="vec-coord-label">LAT</span>
                                        <span className="vec-coord-value">{coords[1].toFixed(6)}</span>
                                    </div>
                                    <div className="vec-coord-chip">
                                        <span className="vec-coord-label">LNG</span>
                                        <span className="vec-coord-value">{coords[0].toFixed(6)}</span>
                                    </div>
                                </div>
                            )}

                            {!hasCoords && (
                                <div className="vec-map-empty">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                    <p>No location set for this center.</p>
                                </div>
                            )}

                            {hasCoords && loadError && (
                                <div className="vec-map-error">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                    <p>Map failed to load.</p>
                                </div>
                            )}

                            {hasCoords && !isLoaded && !loadError && (
                                <div className="vec-map-loading">
                                    <div className="vec-loader" />
                                    <p>Loading map…</p>
                                </div>
                            )}

                            {hasCoords && isLoaded && !loadError && (
                                <GoogleMap
                                    mapContainerStyle={MAP_CONTAINER_STYLE}
                                    center={mapCenter}
                                    zoom={14}
                                    options={{
                                        streetViewControl: false,
                                        mapTypeControl: false,
                                        fullscreenControl: false,
                                        zoomControl: true,
                                        scrollwheel: false,
                                    }}
                                >
                                    <Marker position={markerPos} />
                                </GoogleMap>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ViewExperienceCenter;